import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './MultiSelectDropdown.module.css';
import { ChevronIcon } from './selectIcons';

interface SingleSelectOption {
  value: string;
  label: string;
  /** アイコン画像URL（ラベルの前に表示, オプション） */
  icon?: string;
  /** ラベルの前に表示する任意の React ノード (SVG アイコンなど)。icon より優先 */
  iconNode?: ReactNode;
  /** ラベルの直後に表示する小さなタグ。固定解像度 "1200×628" など */
  tag?: string;
  /** タグの見た目。filled (既定) = 枠線なしの白抜き塗り (確定値)、outline = 枠線のみ (下限など確定していない値) */
  tagVariant?: 'filled' | 'outline';
  /** アイコン画像URL（ラベルの後ろに表示, オプション。お気に入り★など） */
  iconAfter?: string;
  /** 区切り線 (非選択・非ホバー)。true のとき value / label は無視される */
  divider?: boolean;
  /** 右側に表示する丸バッジ (ポイント数など) */
  badge?: string | number;
  /** ラベル下に表示する補助説明 (2 行構成用) */
  sublabel?: string;
  /** この選択肢だけを無効化する (クリック不可・半透明表示)。disabledTooltip があれば
   *  title 属性で理由を出す。歌モノの想定尺 30 秒未満 option など (計画書
   *  scenario-song-lyrics-bgm-plan.md §5.1.4)。 */
  disabled?: boolean;
  /** disabled=true のときに title 属性で表示する理由。 */
  disabledTooltip?: string;
  /** 商品AIプロファイル推奨 (計画書§5.2/§5.5)。true のとき控えめな「おすすめ」バッジを
   *  ラベル横に表示する。HierarchicalSelect の recommendedBadge と同トーン。 */
  recommended?: boolean;
}

interface SingleSelectDropdownProps {
  label: string;
  options: SingleSelectOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  /** ボタンに選択値のlabelのみ表示（"label: "プレフィックスなし） */
  labelOnly?: boolean;
  /** この値のときはアクティブ状態にしない（デフォルト: ''） */
  defaultValue?: string;
  /** ドロップダウンメニュー（ポータル内）に追加するクラス名 */
  dropdownClassName?: string;
  /** @deprecated dropdownWidth は fitContent に置き換えられました */
  dropdownWidth?: {
    min?: string;
    max?: string;
  };
  /** テキスト幅にフィットさせ、ビューポート内に収める */
  fitContent?: boolean;
  /** 値がデフォルトと一致しているときもラベルではなく選択中の値を表示する
   *  (枠線の強調のみを isActive で切り替えたい場合に使う) */
  alwaysShowSelected?: boolean;
  /** 無効化。トリガーを押せなくし、半透明にする */
  disabled?: boolean;
  /** トリガーボタンに付与する追加クラス */
  triggerClassName?: string;
  /** リストの下端に常時表示するアクション行 (「＋ 新規作成」など)。
   *  スクロールしても隠れないよう sticky で固定され、クリックでドロップダウンは閉じる。 */
  footerAction?: { label: string; onClick: () => void };
  /** 下に十分な余白が無いときは上方向に開く。あわせて max-height を可視領域に収める。
   *  ページ下部に置かれるフォーム内のセレクトなど、はみ出しが起きる場所で有効にする。 */
  flipUp?: boolean;
  /** トリガーの既定スタイル(.button)を外し、triggerClassName のみで見た目を作る。
   *  選択中(値≠defaultValue)は data-active="true" 属性で公開するので、
   *  triggerClassName 側で [data-active="true"] を styling できる。
   *  ポップアップ本体は通常どおり共通サーフェスで描画される。 */
  unstyledTrigger?: boolean;
  /** トリガーの先頭 (表示テキストの左) に置くアイコン。選択肢の各行には出ない。 */
  triggerIcon?: ReactNode;
}

export function SingleSelectDropdown({
  label,
  options,
  value,
  onChange,
  className,
  labelOnly,
  defaultValue = '',
  dropdownClassName,
  dropdownWidth,
  fitContent,
  alwaysShowSelected,
  disabled,
  footerAction,
  flipUp,
  triggerClassName,
  unstyledTrigger,
  triggerIcon,
}: SingleSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    /** flipUp 時のみ使用。上方向に開くときの下端 (fixed bottom) */
    bottom: 0,
    openUp: false,
    maxHeight: 0,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const useFitContent = fitContent || !!dropdownWidth?.min;

  // 開く方向の判定に使う、描画前のおおよその高さ (1 行 36px / 区切り線 9px + 上下パディング)。
  // options は毎レンダリング新しい配列になるため ref 経由で参照し、コールバックの依存を増やさない。
  const estimatedHeightRef = useRef(0);
  estimatedHeightRef.current =
    options.reduce((h, o) => h + (o.divider ? 9 : 36), 8) + (footerAction ? 37 : 0);

  const updateDropdownPosition = useCallback((e?: Event) => {
    // ドロップダウン内部のスクロールは無視（幅リセットを防止）
    // resize イベントでは e.target が window (= 非Node) になるため Node かどうかを確認する
    if (e && e.target instanceof Node && dropdownRef.current && dropdownRef.current.contains(e.target)) return;
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const margin = 8;
      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      // flipUp 時のみ: 下に十分な余白が無く、上のほうが広ければ上方向に開く
      const desired = Math.min(300, estimatedHeightRef.current);
      const openUp = !!flipUp && spaceBelow < desired && spaceAbove > spaceBelow;
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        bottom: window.innerHeight - rect.top + 4,
        openUp,
        maxHeight: Math.max(0, (openUp ? spaceAbove : spaceBelow) - 4),
      });
    }
  }, [flipUp]);

  // fitContent時: レンダリング後に実際の幅を計測して再配置
  useLayoutEffect(() => {
    if (!isOpen || !useFitContent || !dropdownRef.current || !buttonRef.current) return;

    const margin = 16;
    const viewportWidth = window.innerWidth;
    const maxAvailable = viewportWidth - margin * 2;
    const el = dropdownRef.current;
    const rect = buttonRef.current.getBoundingClientRect();

    // 自然な幅を取得（max-widthで制限）。
    // box-sizing:border-box では width に border/スクロールバー分が含まれるが、
    // scrollWidth は content+padding のみ(border・スクロールバーを含まない)。
    // そのまま代入すると中身が右端で欠けるため、その差分を足してから設定する。
    // scrollWidth は整数に切り捨てられるため、テキスト幅が小数 (例 154.6px) のとき 154px を設定すると
    // 最長行が折り返してしまう (ラベル + タグの行で「横 / 長」のように割れる)。
    // 初回は width:max-content で描画されているので、実寸 (小数) を切り上げて +1px の余裕を取る。
    const borderAndScrollbar = el.offsetWidth - el.clientWidth;
    const measured = el.style.width === 'max-content'
      ? Math.ceil(el.getBoundingClientRect().width) + 1
      : el.scrollWidth + borderAndScrollbar;
    const naturalWidth = Math.min(measured, maxAvailable);
    el.style.width = `${naturalWidth}px`;

    // ボタン中央を基準に配置し、画面内に収める
    const buttonCenter = rect.left + rect.width / 2;
    let left = buttonCenter - naturalWidth / 2;
    left = Math.max(margin, Math.min(left, viewportWidth - naturalWidth - margin));
    el.style.left = `${left}px`;
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      updateDropdownPosition();
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  const optionNodes = (
    <>
      {options.map((option, index) => (
        option.divider ? (
          <div key={`divider-${index}`} className={styles.divider} role="separator" aria-hidden="true" />
        ) : (
          <div
            key={option.value}
            className={`${styles.option} ${option.value === value ? styles.optionSelected : ''}`}
            aria-disabled={option.disabled || undefined}
            title={option.disabled ? option.disabledTooltip : undefined}
            onClick={() => {
              if (option.disabled) return;
              onChange(option.value);
              setIsOpen(false);
            }}
            style={option.disabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
          >
            {option.iconNode
              ? <span className={styles.optionIconNode} aria-hidden="true">{option.iconNode}</span>
              : option.icon && <img src={option.icon} alt="" className={styles.optionIcon} />}
            {option.sublabel ? (
              <span className={styles.optionLabelGroup}>
                <span>{option.label}</span>
                <span className={styles.optionSublabel}>{option.sublabel}</span>
              </span>
            ) : option.label}
            {option.tag && <span className={`${styles.optionTag} ${option.tagVariant === 'outline' ? styles.optionTagOutline : ''}`}>{option.tag}</span>}
            {option.iconAfter && <img src={option.iconAfter} alt="" className={styles.optionIconAfter} />}
            {option.recommended && <span className={styles.recommendedBadge}>おすすめ</span>}
            {option.badge !== undefined && <span className={styles.optionBadge}>{option.badge}</span>}
          </div>
        )
      ))}
    </>
  );

  const selectedOption = options.find(opt => opt.value === value);
  const isDefault = value === defaultValue;
  const selectedIcon = selectedOption?.icon;
  const selectedIconAfter = selectedOption?.iconAfter;
  const isActive = value !== defaultValue;

  // ボタン表示: アイコンを「ラベル:」の後に配置
  const renderButtonContent = () => {
    if (isDefault && !alwaysShowSelected) {
      return <span className={styles.buttonLabel}>{label}</span>;
    }
    const renderLabelGroup = (opt: SingleSelectOption) => (
      <>
        {opt.sublabel ? (
          <span className={styles.optionLabelGroup}>
            <span>{opt.label}</span>
            <span className={styles.optionSublabel}>{opt.sublabel}</span>
          </span>
        ) : <>{opt.label}</>}
        {opt.tag && <span className={`${styles.optionTag} ${opt.tagVariant === 'outline' ? styles.optionTagOutline : ''}`}>{opt.tag}</span>}
      </>
    );
    const renderLeadingIcon = (opt: SingleSelectOption) => (
      opt.iconNode
        ? <span className={styles.optionIconNode} aria-hidden="true">{opt.iconNode}</span>
        : selectedIcon && <img src={selectedIcon} alt="" className={styles.optionIcon} />
    );
    if (labelOnly && selectedOption) {
      return (
        <span className={styles.buttonLabel}>
          {renderLeadingIcon(selectedOption)}
          {renderLabelGroup(selectedOption)}
          {selectedIconAfter && <img src={selectedIconAfter} alt="" className={styles.optionIconAfter} />}
          {selectedOption.badge !== undefined && <span className={styles.optionBadge}>{selectedOption.badge}</span>}
        </span>
      );
    }
    if (selectedOption) {
      return (
        <span className={styles.buttonLabel}>
          {label}: {renderLeadingIcon(selectedOption)}
          {renderLabelGroup(selectedOption)}
          {selectedIconAfter && <img src={selectedIconAfter} alt="" className={styles.optionIconAfter} />}
          {selectedOption.badge !== undefined && <span className={styles.optionBadge}>{selectedOption.badge}</span>}
        </span>
      );
    }
    return <span className={styles.buttonLabel}>{label}</span>;
  };

  return (
    <div ref={containerRef} className={`${styles.container} ${className || ''}`}>
      <button
        ref={buttonRef}
        type="button"
        className={unstyledTrigger
          ? (triggerClassName || '')
          : `${styles.button} ${isActive ? styles.active : ''} ${triggerClassName || ''}`}
        onClick={() => { if (!disabled) setIsOpen(!isOpen); }}
        disabled={disabled}
        data-active={isActive ? 'true' : undefined}
      >
        {triggerIcon && <span className={styles.triggerIcon} aria-hidden="true">{triggerIcon}</span>}
        <span className={styles.buttonText}>
          {renderButtonContent()}
        </span>
        <span className={styles.arrow}><ChevronIcon open={isOpen} /></span>
      </button>
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className={`modal-surface ${styles.dropdown} ${useFitContent ? styles.dropdownFit : ''} ${footerAction ? styles.dropdownWithFooter : ''} ${dropdownClassName || ''}`}
          style={{
            position: 'fixed',
            top: dropdownPosition.openUp ? undefined : dropdownPosition.top,
            bottom: dropdownPosition.openUp ? dropdownPosition.bottom : undefined,
            left: dropdownPosition.left,
            width: useFitContent ? 'max-content' : undefined,
            minWidth: useFitContent ? undefined : dropdownPosition.width,
            maxWidth: useFitContent ? 'calc(100vw - 32px)' : dropdownWidth?.max,
            maxHeight: flipUp ? Math.min(300, dropdownPosition.maxHeight) : undefined,
            '--dropdown-top': `${dropdownPosition.top}px`,
          } as React.CSSProperties}
        >
          {/* footerAction 時はスクロール領域を内側に分け、フッタが選択肢に重ならないようにする */}
          {footerAction ? <div className={styles.optionsScroll}>{optionNodes}</div> : optionNodes}
          {footerAction && (
            <div
              className={styles.footerAction}
              onClick={() => {
                setIsOpen(false);
                footerAction.onClick();
              }}
            >
              {footerAction.label}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
